import React, { useContext } from 'react';
import useProfileFieldValue from '@ubeya/shared/hooks/account/useProfileFieldValue';
import { generateAuditLogBody, SETTINGS_AUDIT_LOG_TYPES } from '@ubeya/shared/utils/audit-logs';
import { ReactComponent as JobIcon } from '@ubeya/shared-web/assets/SidebarIcons/Modules/sidebar-client.svg';
import { ReactComponent as ClockIcon } from '@ubeya/shared-web/assets/clock.svg';
import { ReactComponent as ShiftIcon } from '@ubeya/shared-web/assets/calendar.svg';
import { ReactComponent as EmployeesIcon } from '@ubeya/shared-web/assets/employee.svg';
import { ReactComponent as AppIcon } from '@ubeya/shared-web/assets/app.svg';
import { ReactComponent as SettingsIcon } from '@ubeya/shared-web/assets/configure.svg';
import ConfigContext from '../../contexts/ConfigContext';
import PrivilegesContext from '../../contexts/PrivilegesContext';
import privileges from '../../privileges';
import AuditLogContainer from './AuditLogContainer';

const getAuditLogIcon = (type) => {
  const iconMap = {
    // Timesheet related
    TimesheetTaskFieldUpdated: ClockIcon,
    UpdatePayrollField: ClockIcon,
    ResetTimesheetTime: ClockIcon,
    CreateTimesheetEndTime: ClockIcon,
    CreateTimesheetCheckInTime: ClockIcon,
    CreateTimesheetCheckOutTime: ClockIcon,
    ClientCreateTimesheetEndTime: ClockIcon,
    CreateTimesheetStartTime: ClockIcon,
    ClientCreateTimesheetStartTime: ClockIcon,
    ClientCreateTimesheetCheckInTime: ClockIcon,
    ClientCreateTimesheetCheckOutTime: ClockIcon,
    AutoFillTimesheet: ClockIcon,
    AutoClockedOut: ClockIcon,
    BreakDeleted: ClockIcon,
    UpdateEmployeeSlotPayroll: ClockIcon,
    UpdateShiftPayroll: ClockIcon,
    UpdateEmployeePayroll: ClockIcon,
    UpdateEmployeePositionPayroll: ClockIcon,

    // Job related
    UpdateJobPayroll: JobIcon,

    // Shift related
    EmployeeBroadcastForShift: ShiftIcon,
    ApprovedWorkingHours: ShiftIcon,
    UnApprovedWorkingHours: ShiftIcon,
    EmployeeBookedForShift: ShiftIcon,
    EmployeeUnBookedForShift: ShiftIcon,
    EmployeeUnBookedForShiftByAccount: ShiftIcon,
    EmployeeRebookedForShiftByAccount: ShiftIcon,
    EmployeeRequestReleaseFromShift: ShiftIcon,
    EmployeeChangeShiftAutoLogUseCase: ShiftIcon,
    EmployeeAutoBookedForShift: ShiftIcon,
    ProjectCreated: ShiftIcon,
    ProjectDeleted: ShiftIcon,
    ProjectCaptainTimesheetStartTimeUpdated: ShiftIcon,
    ProjectCaptainTimesheetEndTimeUpdated: ShiftIcon,
    ProjectCaptainTimesheetCheckInTimeUpdated: ShiftIcon,
    ProjectCaptainTimesheetCheckOutTimeUpdated: ShiftIcon,
    ProjectInvoiceIssue: ShiftIcon,
    ProjectArchived: ShiftIcon,
    ProjectUnArchived: ShiftIcon,
    ProjectUpdated: ShiftIcon,
    ShiftUpdated: ShiftIcon,
    ShiftCheckAvailabilityAutoLog: ShiftIcon,

    // Application related
    ApplicationCreated: AppIcon,

    // Employee related
    EmployeeDeleted: EmployeesIcon,
    EmployeeRestored: EmployeesIcon,
    EmployeeCreated: EmployeesIcon,
    EmployeeUpdated: EmployeesIcon,
    EmployeeBaseWageUpdatedBySystem: EmployeesIcon,
    EmployeeDeactivated: EmployeesIcon,
    EmployeeActivated: EmployeesIcon,
    EmployeeLeftCompany: EmployeesIcon,
    EmployeeCreatedByAPI: EmployeesIcon,
    EmployeeDeletedByAPI: EmployeesIcon,
    EmployeeSMSInvited: EmployeesIcon,
    EmployeePhotoVerified: EmployeesIcon,
    EmployeePhotoUnverified: EmployeesIcon,
    AdminUploadedEmployees: EmployeesIcon,
    AdminUploadedEmployeesJobs: EmployeesIcon,
    RequestDeclined: EmployeesIcon,
    RequestsDeclined: EmployeesIcon,
    IsHostAccountActivated: EmployeesIcon,
    RequestApproved: EmployeesIcon,
    RequestsApproved: EmployeesIcon,
    ...Object.fromEntries(SETTINGS_AUDIT_LOG_TYPES.map((settingsType) => [settingsType, SettingsIcon])),
    AccountContractCreated: EmployeesIcon,
    AccountContractUpdated: EmployeesIcon,
    AccountContractDeleted: EmployeesIcon,
    EmployeeAssignedContract: EmployeesIcon,
    EmployeeContractAdjusted: EmployeesIcon,
    EmployeePtoAllowanceAdjusted: EmployeesIcon,
    AdminAddedEmployeePto: EmployeesIcon,
    AdminDeletedEmployeePto: EmployeesIcon,
    AdminUsedAllPtoBalance: EmployeesIcon,
    AdminApprovedHolidayRequest: EmployeesIcon,
    AdminApprovedSickLeaveRequest: EmployeesIcon,
    AssignedEmployeeAsEventCaptain: EmployeesIcon,
    RemovedEmployeeAsEventCaptain: EmployeesIcon,
    AssignedEmployeeSingleArea: EmployeesIcon,
    AssignedEmployeeMultipleAreas: EmployeesIcon,
    RemovedEmployeeSingleArea: EmployeesIcon,
    RemovedEmployeeMultipleAreas: EmployeesIcon,
    AssignedEmployeeShiftCaptain: EmployeesIcon,
    RemovedEmployeeShiftCaptain: EmployeesIcon,
    AssignedEmployeeSingleTask: EmployeesIcon,
    AssignedEmployeeMultipleTasks: EmployeesIcon,
    RemovedEmployeeSingleTask: EmployeesIcon,
    RemovedEmployeeMultipleTasks: EmployeesIcon,
    EmployeeWeeklyPreferencesUpdated: EmployeesIcon,

    // Contract leave management (CLM)
    LeaveContractCreated: EmployeesIcon,
    LeaveContractUpdated: EmployeesIcon,
    LeaveContractDeleted: EmployeesIcon,
    EmployeeAssignedLeaveContract: EmployeesIcon,
    EmployeeLeaveContractUpdated: EmployeesIcon,
    EmployeeLeaveContractRemoved: EmployeesIcon,
    EmployeeLeaveContractTerminated: EmployeesIcon,
    EmployeeLeaveContractChanged: EmployeesIcon,
    EmployeeLeaveYearReset: EmployeesIcon,
    EmployeeLeaveBalancesPaidOut: EmployeesIcon,
    EmployeeLeaveAllowanceAdjusted: EmployeesIcon,
    AdminAddedEmployeeLeave: EmployeesIcon,
    AdminDeletedEmployeeLeave: EmployeesIcon,
    EmployeeLeaveSpecialDayAccrued: EmployeesIcon,
    EmployeeLeaveContractForfeitOnTermination: EmployeesIcon,
    AdminUnbookedShiftAsLeave: EmployeesIcon,
    EmployeeCommentCreated: EmployeesIcon,
    EmployeeCommentDeleted: EmployeesIcon
  };

  return iconMap[type] || null;
};

const AuditLog = ({ type, data }) => {
  const { t, timeFormat, shortDateMonthFormat, dateFormat, currency } = useContext(ConfigContext);
  const { getProfileFieldValue } = useProfileFieldValue();

  const myPrivileges = useContext(PrivilegesContext);
  const hasSensitiveDataAccess = !!myPrivileges[privileges.SENSITIVE_DATA_ACCESS];

  const Icon = getAuditLogIcon(type);
  if (!Icon) return null;

  const bodyData = {
    t,
    type,
    data,
    timeFormat,
    shortDateMonthFormat,
    dateFormat,
    currency,
    hasSensitiveDataAccess
  };

  // Special case for employee updates that require the profile field value
  if (type === 'EmployeeUpdated') {
    bodyData.getProfileFieldValue = getProfileFieldValue;
  }

  const { text, values } = generateAuditLogBody(bodyData);

  return <AuditLogContainer values={values} text={text} createdAt={data.createdAt} Icon={Icon} />;
};

export default AuditLog;
